import { call, put, select, takeLatest, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  componentApi,
  SearchComponentsRequest,
  SearchComponentsResponse,
  Component,
  CreateComponentRequest,
  UoM
} from '../../services/componentApi';
import {
  fetchComponentsStart,
  fetchComponentsSuccess,
  fetchComponentsFailure,
  fetchComponentByIdStart,
  fetchComponentByIdSuccess,
  fetchComponentByIdFailure,
  createComponentStart,
  createComponentSuccess,
  createComponentFailure,
  updateComponentStart,
  updateComponentSuccess,
  updateComponentFailure,
  deleteComponentStart,
  deleteComponentSuccess,
  deleteComponentFailure,
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,
  setComponentValidationErrors
} from '../reducers/componentSlice';
import { RootState } from '../index';

// Helper function to extract data from API responses
const extractData = (response: any): any => {
  console.log('Component Saga: Processing API response:', response);
  
  // Parse response body if it's a string
  let parsedResponse;
  if (response.body && typeof response.body === 'string') {
    try {
      parsedResponse = JSON.parse(response.body);
      console.log('Component Saga: Parsed response body:', parsedResponse);
    } catch (error) {
      console.error('Component Saga: Failed to parse response body:', error);
      parsedResponse = response;
    }
  } else {
    parsedResponse = response;
  }
  
  // Extract the data
  let data;
  if (parsedResponse && parsedResponse.data) {
    data = parsedResponse.data;
    console.log('Component Saga: Using parsedResponse.data:', data);
  } else if (Array.isArray(parsedResponse)) {
    data = parsedResponse;
    console.log('Component Saga: Using whole parsedResponse as array:', data);
  } else {
    data = parsedResponse;
    console.log('Component Saga: Using parsedResponse as-is:', data);
  }
  
  console.log('Component Saga: Final extracted data:', data);
  return data;
};

// Component Sagas
function* fetchComponentsApi(action: PayloadAction<SearchComponentsRequest | undefined>): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    const { componentPagination, componentSearchTerm, componentActiveFilter } = state.component;
    
    const params: SearchComponentsRequest = action.payload || {
      pageNum: componentPagination.currentPage,
      pageSize: componentPagination.pageSize,
      filter: {
        ...(componentSearchTerm && { Name: componentSearchTerm }),
        ...(componentActiveFilter !== null && { Active: componentActiveFilter }),
      },
    };
    
    console.log('🔍 [ComponentSaga] Fetching components with params:', params);
    console.log('🔍 [ComponentSaga] API base URL:', process.env.REACT_APP_API_GATEWAY_URL);
    
    // Use search endpoint for filtered/paginated results
    const response: SearchComponentsResponse = yield call(componentApi.searchComponents, params);
    console.log('✅ [ComponentSaga] Components fetched successfully:', response);
    yield put(fetchComponentsSuccess(response));
  } catch (error: any) {
    console.error('❌ [ComponentSaga] Failed to fetch components:', error);
    yield put(fetchComponentsFailure(error.message || 'Failed to fetch components'));
  }
}

function* fetchComponentByIdApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    const component: Component = yield call(componentApi.getComponentById, action.payload);
    yield put(fetchComponentByIdSuccess(component));
  } catch (error: any) {
    yield put(fetchComponentByIdFailure(error.message || 'Failed to fetch component'));
  }
}

function* createComponentApi(action: PayloadAction<CreateComponentRequest>): Generator<any, void, any> {
  try {
    // Validate data first
    const validationErrors = componentApi.validateComponent(action.payload);
    if (validationErrors.length > 0) {
      yield put(setComponentValidationErrors(validationErrors));
      yield put(createComponentFailure('Validation failed'));
      return;
    }

    const component: Component = yield call(componentApi.createComponent, action.payload);
    yield put(createComponentSuccess(component));
  } catch (error: any) {
    yield put(createComponentFailure(error.message || 'Failed to create component'));
  }
}

function* updateComponentApi(action: PayloadAction<Partial<Component>>): Generator<any, void, any> {
  try {
    // Validate data first
    const validationErrors = componentApi.validateComponent(action.payload);
    if (validationErrors.length > 0) {
      yield put(setComponentValidationErrors(validationErrors));
      yield put(updateComponentFailure('Validation failed'));
      return;
    }

    const component: Component = yield call(componentApi.updateComponent, action.payload);
    yield put(updateComponentSuccess(component));
  } catch (error: any) {
    yield put(updateComponentFailure(error.message || 'Failed to update component'));
  }
}

function* deleteComponentApi(action: PayloadAction<string>): Generator<any, void, any> {
  try {
    yield call(componentApi.deleteComponent, action.payload);
    yield put(deleteComponentSuccess(action.payload));
  } catch (error: any) {
    yield put(deleteComponentFailure(error.message || 'Failed to delete component'));
  }
}

function* fetchDropdownsApi(): Generator<any, void, any> {
  try {
    console.log('🔍 [ComponentSaga] Fetching UoM dropdowns...');
    const uomsResponse: UoM[] = yield call(componentApi.getActiveUoMs);
    console.log('✅ [ComponentSaga] UoM dropdowns fetched:', uomsResponse);
    
    yield put(fetchDropdownsSuccess({
      uoms: uomsResponse
    }));
  } catch (error: any) {
    console.error('❌ [ComponentSaga] Failed to fetch dropdown data:', error);
    yield put(fetchDropdownsFailure(error.message || 'Failed to fetch dropdown data'));
  }
}

// Watch for filter changes
function* watchFilters(): Generator<any, void, any> {
  yield takeLatest(['component/setComponentSearchTerm', 'component/setComponentActiveFilter'], function* () {
    yield put(fetchComponentsStart());
  });
}

export default function* componentSaga(): Generator<any, void, any> {
  yield all([
    takeLatest(fetchComponentsStart.type, fetchComponentsApi),
    takeLatest(fetchComponentByIdStart.type, fetchComponentByIdApi),
    takeLatest(createComponentStart.type, createComponentApi),
    takeLatest(updateComponentStart.type, updateComponentApi),
    takeLatest(deleteComponentStart.type, deleteComponentApi),
    takeLatest(fetchDropdownsStart.type, fetchDropdownsApi),
    watchFilters(),
  ]);
} 